/**
 * The Players page: one box per proxy, with the names of everyone on it underneath.
 *
 * Names are rendered as text, never HTML. Clicking one opens the same player drawer used elsewhere.
 * Refreshes every 15 seconds (not while the tab is hidden); if a refresh fails the last good lists
 * stay up, marked as stale.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchOnlineRoster } from "../api";
import { players } from "./PlayerCounts";

export function PlayersTab({ onOpenPlayer }: { onOpenPlayer: (player: string) => void }): JSX.Element {
  const { data, isError } = useQuery({
    queryKey: ["player-roster"],
    queryFn: fetchOnlineRoster,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });

  return (
    <div className="scroll">
      <div className="pad grid" style={{ gap: 14 }}>
        <div className="card" data-stale={isError && data ? true : undefined}>
          <h2>Players online</h2>
          {data ? (
            <div className="tile-n" style={{ color: "var(--ok)" }}>
              {players(data.total)} online
            </div>
          ) : (
            <p className="player-counts-status">{isError ? "Unavailable right now" : "Loading…"}</p>
          )}
          {isError && data && (
            <p className="player-counts-status">Unavailable right now — showing the last known lists</p>
          )}
        </div>

        {data && (
          <div className="grid grid-tiles proxy-boxes" data-stale={isError || undefined}>
            {data.proxies.map((proxy) => (
              <div className="card" key={proxy.id}>
                <h2>
                  {proxy.label} proxy · {players(proxy.players.length)}
                </h2>
                {proxy.players.length === 0 ? (
                  <p className="player-counts-status">Nobody is on this proxy.</p>
                ) : (
                  <ul className="proxy-players">
                    {proxy.players.map((player) => (
                      <li key={player.uuid}>
                        <button type="button" className="player-name" onClick={() => onOpenPlayer(player.uuid)}>
                          {player.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
